import { useState, useEffect } from "react";
import { User } from "@supabase/supabase-js";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { LandingPage } from "@/components/LandingPage";
import { AuthPage } from "@/components/AuthPage";
import { Dashboard } from "@/components/Dashboard";
import { useAuthDebug } from "@/hooks/useAuthDebug";
import { useToast } from "@/hooks/use-toast";

export default function Index() {
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState<"landing" | "auth" | "dashboard">("landing");
  const [redirectTo, setRedirectTo] = useState<string | null>(null);
  const { user, session, isReady, error, debugInfo, refreshAuth } = useAuthDebug();
  const { toast } = useToast();

  // Handle OAuth/Magic Link code exchange on redirect
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const hasCode = !!url.searchParams.get('code');
      const hasAccessToken = url.hash.includes('access_token=');
      if (hasCode || hasAccessToken) {
        supabase.auth.exchangeCodeForSession(window.location.href).then(({ data, error }) => {
          console.log('exchangeCodeForSession result:', { userId: data?.user?.id, error: error?.message });
          if (error) {
            console.error('Code exchange failed:', error.message);
          } else {
            // Clean URL and proceed to dashboard
            window.history.replaceState({}, document.title, window.location.pathname);
            setCurrentView("dashboard");
          }
        });
      }
    } catch (e) {
      console.error('Error during code exchange check:', e);
    }
  }, []);

  // If user was redirected here to login, open auth and store intended path
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const needsLogin = url.searchParams.get('login') === '1';
      const redirect = url.searchParams.get('redirect');
      if (needsLogin && !user) {
        setCurrentView('auth');
        if (redirect) setRedirectTo(redirect);
      }
    } catch (e) {
      console.error('Error parsing login redirect params:', e);
    }
  }, [user, isReady]);


  useEffect(() => {
    console.log('=== Index component with useAuthDebug ===');
    console.log('Auth debug state:', { user: user?.id, isReady, error, debugInfo });
    
    if (!isReady) {
      console.log('Auth state not ready yet...');
      return;
    }
    
    if (error) {
      console.error('Auth error detected:', error);
      toast({
        title: "Authentication Issue",
        description: error,
        variant: "destructive"
      });
    }
    
    if (user && session) {
      console.log('User authenticated, navigating to dashboard');
      setCurrentView("dashboard");
    } else {
      console.log('No authenticated user, showing landing page');
      setCurrentView("landing");
    }
  }, [user, session, isReady, error]);

  // Link historical data so legacy players connect to this auth user
  useEffect(() => {
    if (user && session) {
      (async () => {
        try {
          const { error } = await supabase.rpc('fix_user_historical_data', { p_user_id: user.id });
          if (error) {
            console.warn('fix_user_historical_data error:', error.message);
          } else {
            console.log('fix_user_historical_data executed');
          }
        } catch (e) {
          console.error('fix_user_historical_data exception:', e);
        }
      })();
    }
  }, [user?.id, session]);

  // If a redirect was requested, navigate after successful login
  useEffect(() => {
    if (user && session && redirectTo) {
      navigate(redirectTo);
      // Clean URL
      window.history.replaceState({}, document.title, '/');
      setRedirectTo(null);
    }
  }, [user, session, redirectTo]);

  // Show loading while auth state is being determined
  if (!isReady) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    );
  }

  const handleAuthSuccess = (authenticatedUser: User) => {
    console.log('handleAuthSuccess called with user:', authenticatedUser.id);
    // The useAuthDebug hook will handle state updates automatically
    setCurrentView("dashboard");
  };

  const handleLogout = async () => {
    console.log('handleLogout called');
    try {
      await supabase.auth.signOut();
      setCurrentView("landing");
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (currentView === "auth") {
    return <AuthPage onBack={() => setCurrentView("landing")} onAuthSuccess={handleAuthSuccess} />;
  }

  if (currentView === "dashboard" && user) {
    return <Dashboard user={user} onLogout={handleLogout} />;
  }

  return <LandingPage onGetStarted={() => setCurrentView("auth")} />;
}